﻿import { memo, useEffect } from "react";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Divider } from "@mui/material";
import { Skeleton } from "@mui/material";
import { HubConnectionState } from "@microsoft/signalr";
import { useQuery } from '@tanstack/react-query';
import { faTruck } from "@fortawesome/free-solid-svg-icons";

import { MaterialMovementCardSectionDTO } from "../../../DTOs/Dashboards/MaterialMovementCardDataDTO";
import { dashboardsAjax } from "../../../ajax/DashboardsAjax";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { useSignalRContext } from "../../Contexts/SignalRContext";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { ShiftLockState, isCurrentShift } from "../../../utilities/helpers/shiftLockStateHelpers";
import { LiveIndicatorState } from "../DashboardCard";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { useOnlineStatus } from "../../Hooks/useOnlineStatus";

import { shiftNameToLetter } from "../../../types/Shift";
type MaterialMovementCardProps = {
  isFullScreen?: boolean
  shiftLockState: ShiftLockState | null;
  onLiveIndicatorChange?: (state: LiveIndicatorState) => void;
};

export const MaterialMovementCard: React.FC<MaterialMovementCardProps> = memo(({ isFullScreen, shiftLockState, onLiveIndicatorChange }) => {
  const { selectedDate, selectedShift } = useShiftTimes();
  const { connection } = useSignalRContext();
  const isOnline = useOnlineStatus();
  const queryEnabled = selectedDate.isValid && !!selectedShift;
  const { data = null, isLoading } = useQuery({
    queryKey: ['dashboard', 'materialMovement', selectedDate.toISODate(), selectedShift],
    queryFn: async () => {
      const res = await dashboardsAjax.getMaterialMovementCardData(selectedDate, selectedShift);
      if (ErrorResponse.isErrorResponse(res)) return null;
      return res;
    },
    enabled: queryEnabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const showSkeleton = isLoading || !queryEnabled;
  const isEmpty = !showSkeleton && !data;

  useEffect(() => {
    const connected = connection?.state === HubConnectionState.Connected;
    const _isCurrentShift = isCurrentShift(selectedDate, shiftNameToLetter(selectedShift), shiftLockState);

    onLiveIndicatorChange?.({
      active: connected,
      showLabel: connected && _isCurrentShift,
      tooltipText: _isCurrentShift
        ? "Data displayed for the latest shift"
        : "Data displayed for selected shift",
    });
  }, [connection?.state, onLiveIndicatorChange, selectedDate, selectedShift, shiftLockState]);

  return (
    <div className={`h-full w-full ${isFullScreen ? "md:max-w-[1520px]" : ""}`}>
      <Paper className={`!shadow-none !border-none !bg-theme-surface-2 !w-full text-center w-full h-full !rounded-none overflow-y-auto ${isFullScreen ? "md:p-3" : "p-3"}`}>
        <div className={`transition-all duration-300 ${showSkeleton ? "opacity-100" : "opacity-0"}`}>
          {showSkeleton && ( // Note: All this is just loading placeholder
            <>
              <Skeleton variant="rectangular" animation="wave" height={28} width="100%" className={`!mb-0 py-4 !bg-theme-surface ${isFullScreen ? "md:!py-6" : ""}`} />
              <TableContainer>
                <Table size="small" className="my-0">
                  <TableHead>
                    <TableRow className="bg-theme-surface-2">
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, index) => (
                      <TableRow className="bg-theme-surface-2" key={index}>
                        <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Skeleton animation="wave" variant="rectangular" height={28} width="100%" className={`!mb-0 !bg-theme-surface ${isFullScreen ? "md:!py-6" : ""}`} />
              <TableContainer>
                <Table size="small" className="my-0">
                  <TableHead>
                    <TableRow>
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, index) => (
                      <TableRow className="bg-theme-surface-2" key={index}>
                        <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Skeleton animation="wave" variant="rectangular" height={28} width="100%" className={`!mb-0 !bg-theme-surface ${isFullScreen ? "md:!py-6" : ""}`} />
              <TableContainer>
                <Table size="small" className="my-0">
                  <TableHead>
                    <TableRow className="bg-theme-surface-2">
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow className="bg-theme-surface-2">
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              <Skeleton animation="wave" variant="rectangular" height={28} width="100%" className={`!mb-0 !bg-theme-surface ${isFullScreen ? "md:!py-6" : ""}`} />
              <TableContainer>
                <Table size="small" className="my-0">
                  <TableHead>
                    <TableRow className="bg-theme-surface-2">
                      <TableCell className="!p-1"><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className="!p-1"><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, rowIndex) => (
                      <TableRow className="bg-theme-surface-2" key={rowIndex}>
                        {[...Array(2)].map((_, colIndex) => (
                          <TableCell key={colIndex} className={`!py-1${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </div>
        {isEmpty && (
          <DashboardCardEmptyState
            isFullScreen={isFullScreen}
            icon={faTruck}
            title={isOnline ? "No material movement" : "Not available offline"}
            subtitle={isOnline
              ? "There's no material movement recorded for the selected date and shift."
              : "Material movement is totalled by the server, so it cannot be shown while offline. Entries made offline are counted once you reconnect."}
          />
        )}
        {!isEmpty && (
          <div className={`transition-all min-w-[420px] ${!showSkeleton ? "opacity-100 fade-up" : "opacity-0"}`}>
            <Section section={data?.production} isCardFullScreen={isFullScreen} />
            <Section section={data?.inPit} isCardFullScreen={isFullScreen} />
            <Section section={data?.rehandle} isCardFullScreen={isFullScreen} />
            <Section section={data?.crushing} isCardFullScreen={isFullScreen} showAfternoon={true} />

            <div className="flex font-bold border-y border-theme-table-border bg-theme-table-row-even mt-2 text-theme-table-row-contrast">
              <span
                title="Total Material Movement"
                className={`flex-shrink-0 basis-[40%] max-w-[40%] text-right px-1 border-x border-theme-table-border truncate text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                Total Material Movement
              </span>
              <span
                className={`flex-shrink-0 basis-[20%] max-w-[20%] text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {data?.totalMaterialMovement?.measurement}
              </span>
              <span
                className={`flex-shrink-0 basis-[20%] max-w-[20%] text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {data?.totalMaterialMovement?.day?.toLocaleString('en-US')}
              </span>
              <span
                className={`flex-shrink-0 basis-[10%] max-w-[10%] text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {data?.totalMaterialMovement?.night?.toLocaleString('en-US')}
              </span>
              <span
                className={`flex-shrink-0 basis-[10%] max-w-[10%] text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {data?.totalMaterialMovement?.both?.toLocaleString('en-US')}
              </span>
            </div>

            <Divider className="!my-6" />

            <Typography
              variant="subtitle1"
              className={`bg-theme-primary text-theme-primary-contrast border border-theme-table-heading-border !font-bold !mt-4 !mb-0 ${isFullScreen ? "md:!text-2xl md:!py-2" : ""
                }`}
            >
              Production Excavator Locations
            </Typography>

            <div className="flex font-bold border-b border-theme-table-heading-border bg-theme-table-heading text-theme-table-heading-contrast ">
              <span
                title="Plant"
                className={`flex-1 text-left border-x border-theme-table-heading-border px-1 truncate text-sm md:text-base ${isFullScreen ? "md:text-xl" : ""
                  }`}
              >
                Plant
              </span>
              <span
                title={data?.currentProductionExcavatorLocations.locationLabel}
                className={`flex-1 text-left border-r border-theme-table-heading-border px-1 truncate text-sm md:text-base ${isFullScreen ? "md:text-xl" : ""
                  }`}
              >
                {data?.currentProductionExcavatorLocations.locationLabel}
              </span>
            </div>

            {data?.currentProductionExcavatorLocations.locations.map((loc, i) => (
              <div
                key={i}
                className="flex border-b border-theme-table-border bg-theme-table-row-even text-theme-table-row-contrast text-sm md:text-base"
              >
                <span
                  title={loc.plant}
                  className={`flex-1 px-1 border-x border-theme-table-border truncate text-left ${isFullScreen ? "md:text-lg" : ""
                    }`}
                >
                  {loc.plant}
                </span>
                <span
                  title={loc.locationValue}
                  className={`flex-1 px-1 truncate border-r border-theme-table-border text-left ${isFullScreen ? "md:text-lg" : ""
                    }`}
                >
                  {loc.locationValue}
                </span>
              </div>
            ))}

            <Typography
              variant="subtitle1"
              className={`font-semibold bg-theme-primary text-theme-primary-contrast border border-theme-table-heading-border !font-bold !mb-0 ${isFullScreen ? "md:!text-2xl md:!py-2" : ""
                }`}
            >
              Blasthole Drill Locations
            </Typography>

            <div className="flex font-bold border-b border-theme-table-heading-border text-theme-table-heading-contrast bg-theme-table-heading">
              <span
                title="Plant"
                className={`flex-1 text-left border-x border-theme-table-heading-border px-1 truncate text-sm md:text-base ${isFullScreen ? "md:text-xl" : ""
                  }`}
              >
                Plant
              </span>
              <span
                title={data?.currentBlastholeDrillLocations.locationLabel}
                className={`flex-1 text-left border-r border-theme-table-heading-border px-1 truncate text-sm md:text-base ${isFullScreen ? "md:text-xl" : ""
                  }`}
              >
                {data?.currentBlastholeDrillLocations.locationLabel}
              </span>
            </div>

            {data?.currentBlastholeDrillLocations.locations.map((loc, i) => (
              <div
                key={i}
                className="flex border-b border-theme-table-border bg-theme-table-row-even text-theme-table-row-contrast text-sm md:text-base"
              >
                <span
                  title={loc.plant}
                  className={`flex-1 px-1 border-x border-theme-table-border truncate text-left ${isFullScreen ? "md:text-lg" : ""
                    }`}
                >
                  {loc.plant}
                </span>
                <span
                  title={loc.locationValue}
                  className={`flex-1 px-1 border-r border-theme-table-border truncate text-left ${isFullScreen ? "md:text-lg" : ""
                    }`}
                >
                  {loc.locationValue}
                </span>
              </div>
            ))}
          </div>
        )}
      </Paper>
    </div>
  );
});

const Section: React.FC<{
  section?: MaterialMovementCardSectionDTO;
  isCardFullScreen?: boolean;
  showAfternoon?: boolean;
}> = ({ section, isCardFullScreen, showAfternoon = false }) => {
  if (!section) return null;

  const colClass = (width: string) =>
    `flex-shrink-0 text-right basis-[${width}] max-w-[${width}] px-1 first:border-l border-r border-theme-table-border text-sm md:text-base ${isCardFullScreen ? "md:text-lg" : ""
    }`;

  return (
    <div className="mb-3">
      <Typography
        variant="subtitle1"
        className={`bg-theme-primary border border-theme-table-border text-theme-primary-contrast !font-bold mb-0 ${isCardFullScreen ? "md:!text-2xl md:!py-2" : ""
          }`}
      >
        {section.title}
      </Typography>

      <div className="flex bg-theme-table-heading text-theme-table-heading-contrast font-bold border-b border-theme-table-border">
        <span className={`${colClass("40%")} truncate`}></span>
        <span title="Measurement" className={`${colClass("20%")} truncate`}>Measurement</span>
        <span title="Day" className={`${colClass(showAfternoon ? "10%" : "20%")} truncate`}>Day</span>
        {showAfternoon && <span title="Afternoon" className={`${colClass("10%")} truncate`}>Afternoon</span>}
        <span title="Night" className={`${colClass("10%")} truncate`}>Night</span>
        <span title="Both" className={`${colClass("10%")} truncate`}>{showAfternoon ? "All" : "Both"}</span>
      </div>

      {section.rows.map((row, i) => (
        <div
          key={i}
          className={`flex bg-theme-table-row-even text-theme-table-row-contrast border-b border-theme-table-border ${row.isBold ? "font-bold border-t" : ""
            }`}
        >
          <span title={row.label} className={`${colClass("40%")} truncate`}>{row.label}</span>
          <span className={`${colClass("20%")}`}>{row.measurement}</span>
          <span className={`${colClass(showAfternoon ? "10%" : "20%")}`}>{row.day?.toLocaleString('en-US')}</span>
          {showAfternoon && (<span className={`${colClass("10%")}`}>{row.afternoon?.toLocaleString('en-US')}</span>)}
          <span className={`${colClass("10%")}`}>{row.night?.toLocaleString('en-US')}</span>
          <span className={`${colClass("10%")}`}>{row.both?.toLocaleString('en-US')}</span>
        </div>
      ))}
    </div>
  );
};
